import React, { FC, memo } from "react";
import Head from "next/head";
import { normalizeURL } from "helpers/seo";

/** we don't want styled-components to have anything to do with the document head so we're using the plain object here */
import { theme as activeTheme } from "config/themes";

interface MetaTagsProps {
    googleAnalyticsId?: string;
    metaDescription?: string;
    description?: string;
    title?: string;
    socialMediaImage?: string;
    canonicalUrl: string;
    heroImage?: string;
    googleSearchConsole?: string;
    faceBookPixel?: string;
}

export const MetaTags: FC<MetaTagsProps> = memo(
    ({
        metaDescription,
        googleAnalyticsId = "UA-2448665-1",
        description,
        title,
        socialMediaImage,
        canonicalUrl,
        heroImage,
        googleSearchConsole,
        faceBookPixel
    }) => {
        const metaDesc = metaDescription || description;
        const formattedCanonicalUrl = normalizeURL(canonicalUrl);

        return (
            <Head>
                <meta charSet="utf-8" />
                <title>{title}</title>
                <link
                    rel="preload"
                    as="font"
                    href="/static/fonts/Lato/Lato-Regular.woff2"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    as="font"
                    href="/static/fonts/Lato/Lato-Black.woff2"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    as="font"
                    href="/static/fonts/Roboto/RobotoSlab-Regular.ttf"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    as="font"
                    href="/static/fonts/Roboto/RobotoSlab-Black.ttf"
                    crossOrigin=""
                />
                {heroImage && <link rel="preload" as="image" href={heroImage} />}
                <link rel="manifest" href="/static/manifest.json" />
                {formattedCanonicalUrl && <link rel="canonical" href={formattedCanonicalUrl} />}
                <link
                    rel="shortcut icon"
                    type="image/png"
                    href={`/static/images/favicon.ico?version=${process.env.VERSION}`}
                />
                <link
                    rel="icon"
                    sizes="192x192"
                    href={`/static/images/chrome-favicon-192x192.png?version=${process.env.VERSION}`}
                />
                <link
                    rel="apple-touch-icon"
                    href={`/static/images/apple-touch-icon-192x192.png?version=${process.env.VERSION}`}
                />
                <link
                    rel="apple-touch-icon-precomposed"
                    href={`/static/images/apple-touch-icon-192x192.png?version=${process.env.VERSION}`}
                />

                <meta httpEquiv="x-ua-compatible" content="ie=edge" />

                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
                <meta name="theme-color" content={`${activeTheme.colors.primary[700]}`} />
                <meta
                    name="msapplication-navbutton-color"
                    content={`${activeTheme.colors.primary[700]}`}
                />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
                <meta name="apple-itunes-app" content="app-id=767341087" />
                <meta name="description" content={metaDesc} />
                <meta name="gaid" content={googleAnalyticsId} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:site" content="@AutoTrack" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                {socialMediaImage && <meta name="twitter:image" content={socialMediaImage} />}
                <meta name="google-site-verification" content={googleSearchConsole} />
                {/* Allows our facebook business account to accept autotrack.nl as verified website */}
                <meta name="facebook-domain-verification" content={faceBookPixel} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                {socialMediaImage && <meta property="og:image" content={socialMediaImage} />}
                {formattedCanonicalUrl && (
                    <meta property="og:url" content={formattedCanonicalUrl} />
                )}
            </Head>
        );
    }
);
